import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export interface ApiClient {
  id: number;
  name: string;
  owner_name: string | null;
  contact_email: string | null;
  description: string | null;
  status: string;
  created_at: string;
}

export interface ApiKey {
  id: number;
  client_id: number;
  name: string;
  key_prefix: string;
  scopes: string[] | null;
  rate_limit_per_min: number;
  quota_per_day: number;
  status: string;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface ApiCallLog {
  id: number;
  client_id: number | null;
  key_id: number | null;
  license_key: string | null;
  endpoint: string | null;
  method: string | null;
  valid: boolean | null;
  reason: string | null;
  status_code: number | null;
  ip: string | null;
  user_agent: string | null;
  latency_ms: number | null;
  created_at: string;
}

export type EnforcementMode = "required" | "optional";

export interface PolicyChange {
  id: number;
  actor_name: string | null;
  action: string;
  detail: string | null;
  level: string | null;
  meta: { kind?: string; from?: EnforcementMode; mode?: EnforcementMode } | null;
  created_at: string;
}

export interface NewClientInput {
  name: string;
  ownerName: string;
  contactEmail: string;
  description: string;
}

export interface NewKeyInput {
  clientId: number;
  name: string;
  rateLimitPerMin: number;
  quotaPerDay: number;
  expiresAt: string | null;
}

export interface CreatedKey {
  key: string;
  record: ApiKey;
}

async function invokeApiKeys<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-api-keys", { body: payload });
  if (error) {
    let message = error.message || "操作失败，请稍后重试";
    try {
      const context = (error as { context?: Response }).context;
      if (context) {
        const parsed = await context.json();
        if (parsed?.error) message = parsed.error;
      }
    } catch {
      // keep original message
    }
    throw new Error(message);
  }
  const result = data as { error?: string } | null;
  if (result?.error) throw new Error(result.error);
  return data as T;
}

interface UseApiKeysResult {
  clients: ApiClient[];
  keys: ApiKey[];
  logs: ApiCallLog[];
  policy: EnforcementMode;
  policyHistory: PolicyChange[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  setPolicy: (mode: EnforcementMode) => Promise<void>;
  createClient: (input: NewClientInput) => Promise<void>;
  updateClient: (clientId: number, input: NewClientInput) => Promise<void>;
  setClientStatus: (clientId: number, status: "active" | "suspended") => Promise<void>;
  deleteClient: (clientId: number) => Promise<void>;
  createKey: (input: NewKeyInput) => Promise<CreatedKey>;
  updateKey: (keyId: number, input: Omit<NewKeyInput, "clientId">) => Promise<void>;
  setKeyStatus: (keyId: number, status: "active" | "revoked") => Promise<void>;
  deleteKey: (keyId: number) => Promise<void>;
}

// 对接方 / API Key 管理：应用、密钥与调用日志统一加载，所有写操作后自动刷新。
export function useApiKeys(): UseApiKeysResult {
  const { user, loading: authLoading } = useCurrentUser();
  const [clients, setClients] = useState<ApiClient[]>([]);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [logs, setLogs] = useState<ApiCallLog[]>([]);
  const [policy, setPolicyState] = useState<EnforcementMode>("required");
  const [policyHistory, setPolicyHistory] = useState<PolicyChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setClients([]);
      setKeys([]);
      setLogs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await invokeApiKeys<{
        clients?: ApiClient[];
        keys?: ApiKey[];
        logs?: ApiCallLog[];
        policy?: EnforcementMode;
        policyHistory?: PolicyChange[];
      }>({
        action: "list",
      });
      setClients(data.clients ?? []);
      setKeys(data.keys ?? []);
      setLogs(data.logs ?? []);
      setPolicyState(data.policy === "optional" ? "optional" : "required");
      setPolicyHistory(data.policyHistory ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "对接方数据加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const setPolicy = useCallback(
    async (mode: EnforcementMode) => {
      await invokeApiKeys({ action: "setPolicy", mode });
      setPolicyState(mode);
    },
    [],
  );

  const createClient = useCallback(
    async (input: NewClientInput) => {
      await invokeApiKeys({ action: "createClient", ...input });
      await load();
    },
    [load],
  );

  const updateClient = useCallback(
    async (clientId: number, input: NewClientInput) => {
      await invokeApiKeys({ action: "updateClient", clientId, ...input });
      await load();
    },
    [load],
  );

  const setClientStatus = useCallback(
    async (clientId: number, status: "active" | "suspended") => {
      await invokeApiKeys({ action: "setClientStatus", clientId, status });
      await load();
    },
    [load],
  );

  const deleteClient = useCallback(
    async (clientId: number) => {
      await invokeApiKeys({ action: "deleteClient", clientId });
      await load();
    },
    [load],
  );

  const createKey = useCallback(
    async (input: NewKeyInput) => {
      const result = await invokeApiKeys<CreatedKey>({ action: "createKey", ...input });
      await load();
      return result;
    },
    [load],
  );

  const updateKey = useCallback(
    async (keyId: number, input: Omit<NewKeyInput, "clientId">) => {
      await invokeApiKeys({ action: "updateKey", keyId, ...input });
      await load();
    },
    [load],
  );

  const setKeyStatus = useCallback(
    async (keyId: number, status: "active" | "revoked") => {
      await invokeApiKeys({ action: "revokeKey", keyId, status });
      await load();
    },
    [load],
  );

  const deleteKey = useCallback(
    async (keyId: number) => {
      await invokeApiKeys({ action: "deleteKey", keyId });
      await load();
    },
    [load],
  );

  return {
    clients,
    keys,
    logs,
    policy,
    policyHistory,
    loading: loading || authLoading,
    error,
    reload: load,
    setPolicy,
    createClient,
    updateClient,
    setClientStatus,
    deleteClient,
    createKey,
    updateKey,
    setKeyStatus,
    deleteKey,
  };
}